"use client";

import type { AgentTemplate, AgentTemplateDocLinks, AgentTemplateIntegrationMap, AgentTemplateVoiceMap } from "./types";

import { useLocalePrefix } from "@/app/_utilities/i18n";
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { getAgentTemplatePath } from "./utils";
import dynamic from "next/dynamic";

const LazyAgentTemplatePopupDialog = dynamic(() =>
  import("./AgentTemplatePopupDialog").then(m => m.AgentTemplatePopupDialog),
);

interface TemplatePopupData {
  docLinks: AgentTemplateDocLinks;
  integrationMap: AgentTemplateIntegrationMap;
  templates: AgentTemplate[];
  voiceMap: AgentTemplateVoiceMap;
}

interface TemplatePopupState {
  openTemplate: (templateId: string) => void;
}

const AgentTemplatePopupContext = createContext<TemplatePopupState | null>(null);

export function useAgentTemplatePopup() {
  const ctx = useContext(AgentTemplatePopupContext);
  if (!ctx) {
    throw new Error("useAgentTemplatePopup must be used within AgentTemplatePopupProvider");
  }
  return ctx;
}

export function AgentTemplatePopupProvider({ children, data }: { children: React.ReactNode; data: TemplatePopupData }) {
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const pushedStateRef = useRef(false);
  const localePrefix = useLocalePrefix();

  const selectedTemplate = selectedTemplateId
    ? (data.templates.find(t => t.template_id === selectedTemplateId) ?? null)
    : null;

  const openTemplate = useCallback(
    (templateId: string) => {
      setSelectedTemplateId(templateId);
      setIsOpen(true);
      pushedStateRef.current = true;
      const template = data.templates.find(t => t.template_id === templateId);
      window.history.pushState(
        { templatePopup: templateId },
        "",
        `${localePrefix}${getAgentTemplatePath(templateId, template?.name)}`,
      );
    },
    [data.templates, localePrefix],
  );

  const closePopup = useCallback(() => {
    setIsOpen(false);

    if (pushedStateRef.current) {
      pushedStateRef.current = false;
      window.history.back();
    }
  }, []);

  // Handle browser back/forward button
  useEffect(() => {
    const handlePopState = () => {
      // Browser navigated away from our pushed state — just close
      if (isOpen) {
        pushedStateRef.current = false;
        setIsOpen(false);
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [isOpen]);

  return (
    <AgentTemplatePopupContext.Provider value={{ openTemplate }}>
      {children}
      {selectedTemplate && (
        <LazyAgentTemplatePopupDialog
          docLinks={data.docLinks}
          integrationMap={data.integrationMap}
          isOpen={isOpen}
          onClose={closePopup}
          template={selectedTemplate}
          voiceMap={data.voiceMap}
        />
      )}
    </AgentTemplatePopupContext.Provider>
  );
}
